import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import { isFormError } from "src/utils/form-error";
import { notify } from "src/components/base/toast";
import Form from "src/components/base/form";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { PrimaryButton } from "src/components/base/button";
import { Divider } from "src/components/base/divider";
import { Heading } from "src/components/base/heading";
import Breadcrumbs from "src/components/base/breadcrumb";

/**
 * The properties for {@link TenantCreate}
 */
export type TenantCreateProps = {};

/**
 * Update tenant view
 */
export default function TenantCreate(props: TenantCreateProps) {
    const [t] = useTranslation("tenant");
    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            name: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.tenants.create({
                    name: value.name,
                });
                if (isFormError(res)) {
                    return {
                        fields: {
                            name: res.error.name_already_used ? t("error.name-already-used") : undefined,
                        },
                    };
                }
                form.reset();

                notify.success(t("toast.successfully-updated"));
                await navigate({ to: "/a/tenants/$tenantId/general", params: { tenantId: res.uuid } });
            },
        },
    });

    return (
        <div className={"flex flex-col gap-6"}>
            <div className={"flex flex-col gap-3"}>
                <Breadcrumbs items={[{ label: t("button.tenants"), to: "/a/tenants" }, { label: t("label.create") }]} />
                <Heading>{t("heading.tenant-create")}</Heading>
                <Divider />
            </div>

            <Form onSubmit={form.handleSubmit} className={"flex max-w-lg flex-col gap-6"}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"name"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.name")}</RequiredLabel>
                                    <Description>{t("description.name")}</Description>
                                    <Input
                                        autoFocus={true}
                                        required={true}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>

                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <PrimaryButton type="submit"> {t("button.create-tenant")}</PrimaryButton>
                </div>
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/create")({
    component: TenantCreate,
});
